import { reduceTapestry } from '../hooks/useTapestryState';
import { fillSlots } from './fillSlots';
import { packLayout } from './tapestryPacker';
import { measureText } from './tapestryText';

/** Replay every content change through T; cached frames never inherit a seek's future. */
export function createTapestryReplay(
  timeline,
  {
    threshold,
    catalog,
    seed,
    canvasHeight,
    keywords,
    tags,
    fontFamily,
    forcedBackgroundId = null,
    measure = measureText,
  }
) {
  const frames = [];
  const fills = new WeakMap();
  // Fonts are loaded before replay. Each word/size needs a DOM measurement
  // only once, including on a large first seek through the cassette.
  const measurements = new Map();
  const measured = (text, size, family) => {
    const key = `${family}|${size}|${text}`;
    if (!measurements.has(key)) measurements.set(key, measure(text, size, family));
    return measurements.get(key);
  };
  const empty = { placements: [], filled: null };

  const frameAt = (index) => {
    while (frames.length <= index) {
      const i = frames.length;
      const input = timeline.read(timeline.times[i]);
      const state = reduceTapestry(input.segments, input.currentSent, threshold, catalog, seed);
      const prior = frames[i - 1];
      const positions = new Map((prior?.placements || []).map((p) => [p.symbolId, { x: p.x, y: p.y, side: p.side }]));
      const placements = packLayout(state, canvasHeight, keywords, positions, fontFamily, measured);
      const background = forcedBackgroundId || state.layout?.backgroundId || null;
      const commit = background
        ? prior?.commit || {
            sent: state.playheadSent,
            onscreen: new Set(placements.map((p) => p.sentence).filter(Boolean)),
          }
        : null;
      frames.push({ state, placements, commit, background, filled: null });
    }
    return frames[index];
  };

  const indexAt = (time) => {
    if (time == null || !Number.isFinite(time)) return -1;
    let lo = 0;
    let hi = timeline.times.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (timeline.times[mid] <= time) lo = mid + 1;
      else hi = mid;
    }
    return lo - 1;
  };

  return {
    indexAt,
    async prepare(time, detail = null, cancelled = () => false) {
      const index = indexAt(time);
      while (frames.length <= index) {
        if (cancelled()) return false;
        frameAt(Math.min(index, frames.length + 15));
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
      if (cancelled()) return false;
      this.at(time, detail);
      return true;
    },
    peek(time, detail = null) {
      const index = indexAt(time);
      if (index < 0) return empty;
      const frame = frames[index];
      if (!frame) return null;
      if (!detail?.layout || frame.background !== detail.id) return frame;
      return fills.get(detail)?.[index] || null;
    },
    at(time, detail = null) {
      const index = indexAt(time);
      if (index < 0) return empty;
      const frame = frameAt(index);
      if (!detail?.layout || frame.background !== detail.id) return frame;

      // A different layout discards its slot memory. Backgrounds sharing a
      // layout keep it, just as they do during uninterrupted playback.
      let history = fills.get(detail);
      if (!history) {
        history = [];
        fills.set(detail, history);
      }
      while (history.length <= index) {
        const i = history.length;
        const step = frames[i];
        const sameLayout =
          forcedBackgroundId ||
          step.background === detail.id ||
          (step.background && step.state.layout?.layoutId === detail.layout.id);
        const filled = sameLayout
          ? fillSlots(detail.layout, step.state, step.state.playheadSent, {
              symbolTags: tags,
              symbolKeywords: keywords,
              onscreenSentences: step.commit?.onscreen,
              onscreenCommitSent: step.commit?.sent,
              previous: history[i - 1]?.filled?.assignments,
            })
          : null;
        history.push({ ...step, filled });
      }
      return history[index];
    },
  };
}
